import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials, faqItems } from '@/mocks/movies';

const aboutFaqItems = faqItems.slice(0, 4);

export default function AboutPage() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 91影视看片下载',
    description: '91影视看片下载提供免费电影电视剧在线观看，天美麻花星空影视免费观看电视，高清不缓冲，每日更新。了解我们的品牌故事、用户口碑和联系方式。',
    url: 'https://www.91yingshi.com/about',
    inLanguage: 'zh-CN',
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '91影视看片下载',
    url: 'https://www.91yingshi.com/',
    description: '91影视看片下载 - 天美麻花星空影视免费观看电视，免费影视大全高清追剧平台',
    foundingDate: '2020',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'service@91yingshi.com',
      availableLanguage: ['Chinese'],
    },
    sameAs: [
      'https://weibo.com/91yingshi',
      'https://space.bilibili.com/91yingshi',
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: aboutFaqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.91yingshi.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="w-full px-4 md:px-6 py-8">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          <section className="mb-14">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-50 mb-6">
              关于我们
            </h1>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h2 className="font-heading text-xl font-semibold text-foreground-100 mb-4">
                  品牌介绍
                </h2>
                <div className="space-y-4 text-foreground-300 text-sm leading-relaxed">
                  <p>
                    <strong className="text-foreground-100">91影视看片下载</strong>成立于2020年，是国内领先的免费在线影视平台之一。我们致力于为广大影视爱好者提供最全面、最高清、最流畅的观影体验。作为天美麻花星空影视免费观看电视的官方合作平台，我们拥有海量的正版影视资源。
                  </p>
                  <p>
                    我们的团队由一群热爱电影和技术的年轻人组成，每天都在努力为用户带来最新的影视内容。无论是热播大片还是经典老片，无论是国产剧集还是海外动漫，您都能在91影视看片下载找到。
                  </p>
                  <p>
                    <strong className="text-foreground-100">麻花星空影视免费观看</strong>是我们推出的重点服务，支持4K超高清画质和多音轨切换，让您在家中就能享受影院级的观影体验。我们的播放器采用先进的流媒体技术，确保高清不缓冲，给您流畅的观影体验。
                  </p>
                  <p>
                    作为一个<strong className="text-foreground-100">免费影视大全</strong>平台，我们始终坚持"用户至上"的理念。无需注册、无需付费、无需安装任何软件，只需打开浏览器，即可畅享无限影视内容。我们相信，好的内容应该让更多人看到。
                  </p>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-foreground-100 mb-4">
                  平台优势
                </h2>
                <div className="space-y-3">
                  {[
                    { icon: 'ri-shield-check-line', title: '安全可靠', desc: '所有影片经过严格审核，确保内容安全合规' },
                    { icon: 'ri-speed-up-line', title: '极速加载', desc: '采用CDN加速技术，高清视频秒开不缓冲' },
                    { icon: 'ri-smartphone-line', title: '多端适配', desc: '支持电脑、平板、手机全平台观看' },
                    { icon: 'ri-refresh-line', title: '每日更新', desc: '专业团队全天候更新最新影视资源' },
                    { icon: 'ri-vip-crown-line', title: '完全免费', desc: '无需注册无需付费，打开即看' },
                    { icon: 'ri-hd-line', title: '超清画质', desc: '支持1080P至4K多种清晰度选择' },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200/50"
                    >
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/15 text-primary-400 flex-shrink-0">
                        <i className={item.icon}></i>
                      </div>
                      <div>
                        <h4 className="text-foreground-100 font-semibold text-sm mb-1">{item.title}</h4>
                        <p className="text-foreground-400 text-xs">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="mb-14">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50">
                用户口碑
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {testimonials.map((item, index) => (
                <div
                  key={index}
                  className="p-5 rounded-lg bg-background-100 border border-background-200/50"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: item.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-foreground-300 text-sm leading-relaxed mb-4">
                    &ldquo;{item.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <img
                      src={item.avatar}
                      alt={item.name}
                      className="w-9 h-9 rounded-full object-cover"
                      title={item.name}
                    />
                    <span className="text-foreground-200 text-sm font-medium">{item.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-14">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50">
                联系我们
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="p-5 rounded-lg bg-background-100 border border-background-200/50">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/15 text-primary-400">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <h4 className="text-foreground-100 font-semibold text-sm">电子邮箱</h4>
                      <p className="text-foreground-400 text-sm">service@91yingshi.com</p>
                    </div>
                  </div>
                </div>
                <div className="p-5 rounded-lg bg-background-100 border border-background-200/50">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-500/15 text-accent-400">
                      <i className="ri-wechat-line"></i>
                    </div>
                    <div>
                      <h4 className="text-foreground-100 font-semibold text-sm">微信公众号</h4>
                      <p className="text-foreground-400 text-sm">91影视看片下载</p>
                    </div>
                  </div>
                </div>
                <div className="p-5 rounded-lg bg-background-100 border border-background-200/50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-500/15 text-secondary-400">
                      <i className="ri-time-line"></i>
                    </div>
                    <div>
                      <h4 className="text-foreground-100 font-semibold text-sm">工作时间</h4>
                      <p className="text-foreground-400 text-sm">周一至周日 9:00 - 22:00</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-lg bg-background-100 border border-background-200/50">
                <h3 className="font-heading text-lg font-semibold text-foreground-100 mb-4">
                  给我们留言
                </h3>
                <form
                  className="space-y-4"
                  data-readdy-form
                  id="contact-form"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const form = e.currentTarget as HTMLFormElement;
                    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
                    if (honeypot) {
                      const msgEl = document.getElementById('contact-form-msg');
                      if (msgEl) {
                        msgEl.textContent = '留言已发送，我们会尽快回复您！';
                        msgEl.className = 'text-accent-400 text-sm mt-3';
                      }
                      return;
                    }
                    try {
                      const formData = new FormData(form);
                      const response = await fetch('https://readdy.ai/api/form/d9njsem8mbnljin5a24g', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
                      });
                      const responseText = await response.text();
                      let parsed;
                      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
                      const msgEl = document.getElementById('contact-form-msg');
                      if (response.ok && parsed?.code === 'OK') {
                        if (msgEl) {
                          msgEl.textContent = '留言已发送，我们会尽快回复您！';
                          msgEl.className = 'text-accent-400 text-sm mt-3';
                        }
                        form.reset();
                      } else {
                        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
                        if (msgEl) {
                          msgEl.textContent = serverMsg || '发送失败，请稍后重试';
                          msgEl.className = 'text-primary-400 text-sm mt-3';
                        }
                      }
                    } catch {
                      const msgEl = document.getElementById('contact-form-msg');
                      if (msgEl) {
                        msgEl.textContent = '网络错误，请稍后重试';
                        msgEl.className = 'text-primary-400 text-sm mt-3';
                      }
                    }
                  }}
                >
                  <div className="honeypot-field">
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden readOnly />
                  </div>
                  <div>
                    <label htmlFor="contact-name" className="block text-foreground-300 text-sm mb-1.5">
                      姓名
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-200/50 border border-background-200/70 text-foreground-100 text-sm placeholder:text-foreground-500 focus:outline-none focus:border-primary-500/50 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-foreground-300 text-sm mb-1.5">
                      邮箱
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-200/50 border border-background-200/70 text-foreground-100 text-sm placeholder:text-foreground-500 focus:outline-none focus:border-primary-500/50 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-foreground-300 text-sm mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg bg-background-200/50 border border-background-200/70 text-foreground-100 text-sm placeholder:text-foreground-500 focus:outline-none focus:border-primary-500/50 transition-colors resize-none"
                      placeholder="请输入您的留言内容"
                    ></textarea>
                    <p className="text-foreground-500 text-xs mt-1">最多500个字符</p>
                  </div>
                  <button
                    type="submit"
                    className="px-6 py-2.5 rounded-full bg-primary-500 text-foreground-50 font-medium text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    发送留言
                  </button>
                  <div id="contact-form-msg"></div>
                </form>
              </div>
            </div>
          </section>

          <section className="mb-14">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50">
                常见问题
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {aboutFaqItems.map((item, index) => (
                <div
                  key={index}
                  className="p-5 rounded-lg bg-background-100 border border-background-200/50"
                >
                  <h4 className="font-heading text-foreground-100 font-semibold mb-2">
                    {item.question}
                  </h4>
                  <p className="text-foreground-400 text-sm leading-relaxed">
                    {item.answer}
                  </p>
                </div>
              ))}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}